import { useState, useEffect, useRef } from 'react';
import { UserPlus, Sparkles, AlertTriangle, Info, Trash2, CheckCheck, Bell } from 'lucide-react';
import { fetchApi } from '../api';
import { getSocket } from '../socket';
import { navigateToNotification } from '../shared/notificationTargets';
import { getDevicePrefs, getUserSettings } from '../utils/userSettings';

interface TopbarProps {
  onMenuClick: () => void;
  user: any;
  onNavigateToBilling?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToSettings?: () => void;
}

interface Notification {
  id: number;
  type: 'lead' | 'deal' | 'stock' | 'ai' | 'system';
  title: string;
  message: string;
  /** Legacy path. Kept for old rows; `target` is what navigation uses. */
  link?: string;
  entityType?: string | null;
  entityId?: number | null;
  /** Resolved server-side by shared/notificationTargets.ts. */
  target?: { view?: string | null; entityId?: number | null } | null;
  read: boolean;
  createdAt: string;
}

const getInitials = (name?: string) => {
  if (!name) return '??';
  return name.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();
};

const formatTimeAgo = (dateStr: string) => {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSec < 45) return 'Только что';
  if (diffMin < 60) return `${diffMin} мин. назад`;
  if (diffHours < 24) return `${diffHours} ч. назад`;
  return `${diffDays} дн. назад`;
};

const playNotificationSound = () => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();
    osc1.type = 'sine'; osc2.type = 'sine';
    osc1.frequency.setValueAtTime(587.33, ctx.currentTime);
    osc2.frequency.setValueAtTime(880, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc1.connect(gain); osc2.connect(gain); gain.connect(ctx.destination);
    osc1.start(ctx.currentTime); osc1.stop(ctx.currentTime + 0.15);
    osc2.start(ctx.currentTime + 0.1); osc2.stop(ctx.currentTime + 0.35);
  } catch {}
};

/**
 * Browser-level notification. Its click used to do `window.location.hash = link`
 * with a backend path like `/chats?id=12`, which the router's allowlist rejected,
 * so the notification opened nothing. It now goes through the shared resolver.
 */
const showDesktopNotification = (payload: Notification) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    const notif = new Notification(payload.title, {
      body: payload.message,
      icon: '/favicon.ico',
      tag: `clientix-notification-${payload.id}`,
    });
    notif.onclick = () => {
      window.focus();
      navigateToNotification(payload);
    };
  } catch (e) {
    console.error('[Desktop notification error]', e);
  }
};

export default function Topbar({ onMenuClick, user, onNavigateToBilling, onNavigateToProfile, onNavigateToSettings }: TopbarProps) {
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const notificationWrapRef = useRef<HTMLDivElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [, setLoading] = useState<boolean>(false);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const res = await fetchApi('/notifications');
      if (res && Array.isArray(res.notifications)) { setNotifications(res.notifications); setUnreadCount(res.unreadCount || 0); }
    } catch (e) { console.error('[Topbar] Failed to load notifications:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadNotifications();
    if ('Notification' in window && Notification.permission === 'default') { Notification.requestPermission().catch(() => {}); }
    const socket = getSocket();
    if (socket) {
      const handleNewNotification = (newNotif: Notification) => {
        setNotifications(prev => [newNotif, ...prev]);
        setUnreadCount(prev => prev + 1);
        // The server already withholds the push when "Показывать сразу в браузере"
        // is off; checking here too means the switch takes effect immediately
        // instead of after the settings cache expires.
        if (!getUserSettings().notifyPush) return;
        if (getDevicePrefs().sound) playNotificationSound();
        showDesktopNotification(newNotif);
      };
      socket.on('notification:new', handleNewNotification);
      return () => { socket.off('notification:new', handleNewNotification); };
    }
  }, []);
  useEffect(() => {
    if (!notifOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!notificationWrapRef.current?.contains(event.target as Node)) {
        setNotifOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [notifOpen]);


  const handleLogout = async () => {
    try { await fetchApi('/auth/logout', { method: 'POST' }); } finally { window.location.reload(); }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    window.dispatchEvent(new CustomEvent('global:search', { detail: e.target.value }));
  };

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try { await fetchApi('/notifications/read-all', { method: 'PATCH' }); setNotifications(prev => prev.map(n => ({ ...n, read: true }))); setUnreadCount(0); }
    catch (e) { console.error('[Topbar] Error marking all read:', e); }
  };

  const handleClearAll = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try { await fetchApi('/notifications/all', { method: 'DELETE' }); setNotifications([]); setUnreadCount(0); }
    catch (e) { console.error('[Topbar] Error clearing all:', e); }
  };

  const handleMarkAsRead = async (notif: Notification) => {
    if (!notif.read) {
      try { await fetchApi(`/notifications/${notif.id}/read`, { method: 'PATCH' }); setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n)); setUnreadCount(prev => Math.max(0, prev - 1)); }
      catch (e) { console.error('[Topbar] Error marking item read:', e); }
    }
    // Every notification now leads somewhere: the resolver falls back to the
    // dashboard rather than leaving the click dead.
    setNotifOpen(false);
    navigateToNotification(notif);
  };

  const handleDeleteItem = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try { await fetchApi(`/notifications/${id}`, { method: 'DELETE' }); const target = notifications.find(n => n.id === id); setNotifications(prev => prev.filter(n => n.id !== id)); if (target && !target.read) { setUnreadCount(prev => Math.max(0, prev - 1)); } }
    catch (e) { console.error('[Topbar] Error deleting notification:', e); }
  };

  const renderIcon = (type: Notification['type']) => {
    switch (type) {
      case 'lead': return (<span className="notif-ic" style={{ background: 'var(--blue-soft)', color: 'var(--blue)' }}><UserPlus size={17} /></span>);
      case 'deal': return (<span className="notif-ic" style={{ background: 'var(--green-soft)', color: 'var(--green)' }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 17, height: 17 }}><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg></span>);
      case 'stock': return (<span className="notif-ic" style={{ background: 'var(--amber-soft)', color: 'var(--amber)' }}><AlertTriangle size={17} /></span>);
      case 'ai': return (<span className="notif-ic" style={{ background: 'var(--primary-soft)', color: 'var(--primary)' }}><Sparkles size={17} /></span>);
      default: return (<span className="notif-ic" style={{ background: 'var(--soft)', color: 'var(--muted)' }}><Info size={17} /></span>);
    }
  };

  const filteredNotifications = notifications.filter(n => filter === 'unread' ? !n.read : true);

  return (
    <header className="topbar">
      <button className="burger" onClick={onMenuClick} aria-label="Открыть навигацию"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 12h16M4 18h16" /></svg></button>
      <div className="searchbox" role="search"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg><input aria-label="Поиск клиентов и чатов" id="globalSearch" placeholder="Поиск клиента или чата…" onChange={handleSearchChange} /></div>
      <div className="topbar-right">
        <div ref={notificationWrapRef} style={{ position: 'relative' }} id="bellWrap">
          <button className="icon-btn" aria-label="Уведомления" aria-expanded={notifOpen} aria-controls="notifPanel" onClick={() => { if ('Notification' in window && Notification.permission === 'default') { Notification.requestPermission().catch(() => {}); } setNotifOpen(!notifOpen); setUserMenuOpen(false); }}>
            <Bell size={19} />
            {unreadCount > 0 && (<span className="bell-badge" id="bellBadge">{unreadCount > 99 ? '99+' : unreadCount}</span>)}
          </button>
          <div className={`dropdown notif-dropdown ${notifOpen ? 'open' : ''}`} id="notifPanel" role="dialog" aria-label="Уведомления">
            <div className="notif-header">
              <div className="dd-title">Уведомления {unreadCount > 0 && `(${unreadCount})`}</div>
              <div className="notif-actions">
                {unreadCount > 0 && (<button className="notif-action-btn" onClick={handleMarkAllRead} title="Прочитать все"><CheckCheck size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} />Прочитать все</button>)}
                {notifications.length > 0 && (<button className="notif-action-btn danger" onClick={handleClearAll} title="Очистить все">Очистить</button>)}
              </div>
            </div>
            <div className="notif-tabs">
              <button className={`notif-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>Все ({notifications.length})</button>
              <button className={`notif-tab ${filter === 'unread' ? 'active' : ''}`} onClick={() => setFilter('unread')}>Непрочитанные ({unreadCount})</button>
            </div>
            <div className="notif-list">
              {filteredNotifications.length === 0 ? (<div className="notif-empty">{filter === 'unread' ? 'Нет непрочитанных уведомлений' : 'Уведомлений пока нет'}</div>) : (
                filteredNotifications.map(notif => (
                  <div key={notif.id} className={`notif-item ${!notif.read ? 'unread' : ''}`} onClick={() => handleMarkAsRead(notif)}>
                    {renderIcon(notif.type)}
                    <div className="notif-content"><p><b>{notif.title}: </b>{notif.message}</p><span>{formatTimeAgo(notif.createdAt)}</span></div>
                    {!notif.read && <div className="notif-unread-dot" title="Непрочитано" />}
                    <button className="notif-del-btn" title="Удалить" onClick={(e) => handleDeleteItem(e, notif.id)}><Trash2 size={14} /></button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        <div style={{ position: 'relative' }} id="userWrap">
          <button className="user-chip" aria-label="Меню аккаунта" aria-expanded={userMenuOpen} aria-controls="userMenu" onClick={() => { setUserMenuOpen(!userMenuOpen); setNotifOpen(false); }}>
            <span className="user-av">{getInitials(user?.name)}</span>
            <span className="user-meta"><b>{user?.name || 'Загрузка...'}</b><span>{user?.plan || 'FREE'}</span></span>
          </button>
          <div className={`dropdown ${userMenuOpen ? 'open' : ''}`} id="userMenu" style={{ minWidth: 210 }}>
            <button className="menu-item" onClick={() => { setUserMenuOpen(false); onNavigateToProfile?.(); }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>Профиль</button>
            <button className="menu-item" onClick={() => { setUserMenuOpen(false); onNavigateToBilling?.(); }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>Тариф и оплата</button>
            <button className="menu-item" onClick={() => { setUserMenuOpen(false); onNavigateToSettings?.(); }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></svg>Настройки</button>
            <button className="menu-item danger" onClick={handleLogout}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>Выйти</button>
          </div>
        </div>
      </div>
    </header>
  );
}
